import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { PersonaPresence } from 'office-ui-fabric-react';
import { ResponseType } from '@microsoft/microsoft-graph-client';
import axios from 'axios';
import graphClient from '@graph/graphClient';
import { useAuthStore } from '@stores/authStore';
import { getClientConfig } from '../services/clientConfigService';
var InMemoryCacheType = /*#__PURE__*/ function(InMemoryCacheType) {
    InMemoryCacheType[InMemoryCacheType["STANDART"] = 0] = "STANDART";
    InMemoryCacheType[InMemoryCacheType["DIMENSION"] = 1] = "DIMENSION";
    InMemoryCacheType[InMemoryCacheType["LARGE"] = 2] = "LARGE";
    return InMemoryCacheType;
}(InMemoryCacheType || {});
var _obj;
var inMemoryCacheKeySuffixesGroupedByCacheType = (_obj = {}, _define_property(_obj, 0, function() {
    var dimensions = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : '96x96';
    return 'standart';
}), _define_property(_obj, 1, function() {
    var dimensions = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : '96x96';
    return "".concat(dimensions);
}), _define_property(_obj, 2, function(dimensions) {
    return 'large';
}), _obj);
// Map the legacy cache-type/dimension to the server photo endpoint's `size` token.
var DIMENSION_TO_SIZE = {
    '96x96': '96',
    '120x120': '120',
    '240x240': '240'
};
var _obj1;
// Microsoft Graph photo URLs — used ONLY by the legacy client-side fallback (USER_PHOTO_SERVICE off).
// Encode the UPN so a B2B guest UPN's '#EXT#' isn't truncated at the URL fragment (Graph decodes it back).
var dimensionURLsGroupedByCacheType = (_obj1 = {}, _define_property(_obj1, 0, function(userPrincipalName) {
    var dimensions = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : '96x96';
    return "users/".concat(encodeURIComponent(userPrincipalName.toString()), "/photos/").concat(dimensions, "/$value");
}), _define_property(_obj1, 1, function(userPrincipalName) {
    var dimensions = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : '96x96';
    return "users/".concat(encodeURIComponent(userPrincipalName.toString()), "/photos/").concat(dimensions, "/$value");
}), _define_property(_obj1, 2, function(userPrincipalName, dimensions) {
    return "users/".concat(encodeURIComponent(userPrincipalName.toString()), "/photo/$value");
}), _obj1);
var profilePicturesInMemoryCache = {};
/**
 * In-flight requests, keyed like the value cache. The value cache is written only once a fetch resolves
 * and `callGraph` awaits `getClientConfig()` before fetching, so without this map every caller arriving
 * inside that window opens its own request for the same key. Here that is a correctness bug rather than
 * just waste: `revokePreviousObjectUrl` means a second resolver revokes a blob URL the first has already
 * handed to a mounted <img>, blanking a rendered avatar. Reachable via the getters that are not funnelled
 * through PersonaContextProvider's own in-flight guard — getProfilePicLarge (per 1:1 list item, per
 * attendee) and getProfilePic.
 *
 * Evicted on settle, NOT via `.catch()`: both fetch paths swallow their errors and resolve '' and
 * `getClientConfig` never rejects, so a rejection never occurs — a `.catch()` eviction would never fire,
 * leaking the entry and pinning a transient '' for the session.
 */ var inFlightPhotoRequests = {};
var inFlightPhotoAbortControllers = {};
var inFlightPhotoScopedSignals = {};
var inFlightPhotoUnscopedConsumers = {};
var inFlightPresenceAbortControllers = {};
var isRequestAborted = function isRequestAborted(error) {
    if (!error || (typeof error === "undefined" ? "undefined" : _type_of(error)) !== 'object') return false;
    var e = error;
    return e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError';
};
var revokePreviousObjectUrl = function revokePreviousObjectUrl(blobCacheKey) {
    var prev = profilePicturesInMemoryCache === null || profilePicturesInMemoryCache === void 0 ? void 0 : profilePicturesInMemoryCache[blobCacheKey];
    if (prev && prev !== 'USER_PHOTO_NOT_EXISTS') {
        try {
            window.URL.revokeObjectURL(prev);
        } catch (unused) {}
    }
};
var abortPhotoRequestIfOrphaned = function abortPhotoRequestIfOrphaned(blobCacheKey) {
    var _ref, _inFlightPhotoUnscopedConsumers_blobCacheKey;
    var _inFlightPhotoScopedSignals_blobCacheKey;
    var hasScopedConsumers = ((_ref = (_inFlightPhotoScopedSignals_blobCacheKey = inFlightPhotoScopedSignals[blobCacheKey]) === null || _inFlightPhotoScopedSignals_blobCacheKey === void 0 ? void 0 : _inFlightPhotoScopedSignals_blobCacheKey.size) !== null && _ref !== void 0 ? _ref : 0) > 0;
    var hasUnscopedConsumers = ((_inFlightPhotoUnscopedConsumers_blobCacheKey = inFlightPhotoUnscopedConsumers[blobCacheKey]) !== null && _inFlightPhotoUnscopedConsumers_blobCacheKey !== void 0 ? _inFlightPhotoUnscopedConsumers_blobCacheKey : 0) > 0;
    if (!hasScopedConsumers && !hasUnscopedConsumers) {
        var _inFlightPhotoAbortControllers_blobCacheKey;
        (_inFlightPhotoAbortControllers_blobCacheKey = inFlightPhotoAbortControllers[blobCacheKey]) === null || _inFlightPhotoAbortControllers_blobCacheKey === void 0 ? void 0 : _inFlightPhotoAbortControllers_blobCacheKey.abort();
    }
};
var attachPhotoRequestConsumer = function attachPhotoRequestConsumer(blobCacheKey, signal) {
    var _inFlightPhotoScopedSignals_blobCacheKey;
    if (!signal) {
        var _inFlightPhotoUnscopedConsumers_blobCacheKey;
        inFlightPhotoUnscopedConsumers[blobCacheKey] = ((_inFlightPhotoUnscopedConsumers_blobCacheKey = inFlightPhotoUnscopedConsumers[blobCacheKey]) !== null && _inFlightPhotoUnscopedConsumers_blobCacheKey !== void 0 ? _inFlightPhotoUnscopedConsumers_blobCacheKey : 0) + 1;
        return;
    }
    if (signal.aborted) {
        abortPhotoRequestIfOrphaned(blobCacheKey);
        return;
    }
    var scopedSignals = (_inFlightPhotoScopedSignals_blobCacheKey = inFlightPhotoScopedSignals[blobCacheKey]) !== null && _inFlightPhotoScopedSignals_blobCacheKey !== void 0 ? _inFlightPhotoScopedSignals_blobCacheKey : new Set();
    scopedSignals.add(signal);
    inFlightPhotoScopedSignals[blobCacheKey] = scopedSignals;
    signal.addEventListener('abort', function() {
        scopedSignals.delete(signal);
        abortPhotoRequestIfOrphaned(blobCacheKey);
    }, {
        once: true
    });
};
/**
 * Primary path: fetch an avatar from the App backend photo endpoint (GET /api/user/photo/:userId) via
 * the session axios (Bearer + region auto-attached). No Graph token. Negative-caches ONLY a confirmed
 * 404 (no photo → initials); transient errors are not cached so a blip doesn't blank the avatar for the
 * rest of the session.
 */ var fetchPhotoFromEndpoint = function fetchPhotoFromEndpoint(userPrincipalName, type, dimensions, blobCacheKey, signal) {
    return _async_to_generator(function() {
        var _DIMENSION_TO_SIZE_, size, res, blobUrl, error, _error_response;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    size = type === 2 ? 'large' : (_DIMENSION_TO_SIZE_ = DIMENSION_TO_SIZE[dimensions === null || dimensions === void 0 ? void 0 : dimensions.toString()]) !== null && _DIMENSION_TO_SIZE_ !== void 0 ? _DIMENSION_TO_SIZE_ : '96';
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        3,
                        ,
                        4
                    ]);
                    return [
                        4,
                        axios.get("/api/user/photo/".concat(encodeURIComponent(userPrincipalName.toString())), {
                            params: {
                                size: size
                            },
                            responseType: 'blob',
                            signal: signal,
                            adapter: 'fetch',
                            fetchOptions: {
                                priority: 'low'
                            },
                            // The app-wide `Pragma: no-cache` GET default makes the browser bypass its HTTP cache, which
                            // would re-fetch every avatar (302 + blob) on each reload despite the endpoint's Cache-Control.
                            // null removes the header for avatars only; every other request keeps the global default.
                            headers: {
                                Pragma: null
                            }
                        })
                    ];
                case 2:
                    res = _state.sent();
                    if (signal.aborted) return [
                        2,
                        ''
                    ];
                    blobUrl = window.URL.createObjectURL(res.data);
                    revokePreviousObjectUrl(blobCacheKey);
                    profilePicturesInMemoryCache[blobCacheKey] = blobUrl;
                    return [
                        2,
                        blobUrl
                    ];
                case 3:
                    error = _state.sent();
                    if (isRequestAborted(error) || signal.aborted) return [
                        2,
                        ''
                    ];
                    if ((error === null || error === void 0 ? void 0 : (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === 404) {
                        profilePicturesInMemoryCache[blobCacheKey] = 'USER_PHOTO_NOT_EXISTS';
                    }
                    return [
                        2,
                        ''
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    })();
};
/**
 * Fallback path (USER_PHOTO_SERVICE disabled via /api/client-config): the legacy direct Microsoft Graph
 * avatar fetch with the viewer's delegated token. Kept so photo traffic can be shed off the backend
 * without a redeploy. External (tokenless) users can't use this path — the flag must stay ON once they
 * are activated. Negative-caches on any error (mirrors the prior Graph behavior).
 */ var fetchPhotoFromGraph = function fetchPhotoFromGraph(userPrincipalName, type, dimensions, blobCacheKey, signal) {
    return _async_to_generator(function() {
        var _dimensionURLsGroupedByCacheType_type, accessToken, client, url, res, blobUrl, error;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _state.trys.push([
                        0,
                        3,
                        ,
                        4
                    ]);
                    if (signal.aborted) return [
                        2,
                        ''
                    ];
                    return [
                        4,
                        useAuthStore.getState().getValidGraphToken()
                    ];
                case 1:
                    accessToken = _state.sent();
                    // No token yet — don't negative-cache, so the avatar retries once a token exists.
                    if (!accessToken || signal.aborted) return [
                        2,
                        ''
                    ];
                    client = graphClient.get(accessToken);
                    url = dimensionURLsGroupedByCacheType === null || dimensionURLsGroupedByCacheType === void 0 ? void 0 : (_dimensionURLsGroupedByCacheType_type = dimensionURLsGroupedByCacheType[type]) === null || _dimensionURLsGroupedByCacheType_type === void 0 ? void 0 : _dimensionURLsGroupedByCacheType_type.call(dimensionURLsGroupedByCacheType, userPrincipalName, dimensions);
                    return [
                        4,
                        client.api(url).responseType(ResponseType.BLOB).options({
                            signal: signal
                        }).get()
                    ];
                case 2:
                    res = _state.sent();
                    if (signal.aborted) return [
                        2,
                        ''
                    ];
                    blobUrl = window.URL.createObjectURL(res);
                    revokePreviousObjectUrl(blobCacheKey);
                    profilePicturesInMemoryCache[blobCacheKey] = blobUrl;
                    return [
                        2,
                        blobUrl
                    ];
                case 3:
                    error = _state.sent();
                    if (isRequestAborted(error) || signal.aborted) return [
                        2,
                        ''
                    ];
                    profilePicturesInMemoryCache[blobCacheKey] = 'USER_PHOTO_NOT_EXISTS';
                    return [
                        2,
                        ''
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    })();
};
/**
 * Resolve an avatar, choosing the source from the server-driven USER_PHOTO_SERVICE flag (fetched once
 * per session): on → the backend photo endpoint; off → the legacy client-side Graph fetch. Both write
 * the same in-memory cache key, so only one source is used per session and the cache stays coherent.
 * Single-flight per blobCacheKey — concurrent callers for one key share a request (see
 * inFlightPhotoRequests). Reached only on a value-cache miss; the getters check that first.
 */ var callGraph = function callGraph(userPrincipalName, type) {
    var dimensions = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : '96x96', options = arguments.length > 3 ? arguments[3] : void 0;
    return _async_to_generator(function() {
        var _ref, _inMemoryCacheKeySuffixesGroupedByCacheType_type, blobCacheKeySuffix, blobCacheKey, inFlight, controller, request;
        return _ts_generator(this, function(_state) {
            blobCacheKeySuffix = (_ref = inMemoryCacheKeySuffixesGroupedByCacheType === null || inMemoryCacheKeySuffixesGroupedByCacheType === void 0 ? void 0 : (_inMemoryCacheKeySuffixesGroupedByCacheType_type = inMemoryCacheKeySuffixesGroupedByCacheType[type]) === null || _inMemoryCacheKeySuffixesGroupedByCacheType_type === void 0 ? void 0 : _inMemoryCacheKeySuffixesGroupedByCacheType_type.call(inMemoryCacheKeySuffixesGroupedByCacheType, dimensions)) !== null && _ref !== void 0 ? _ref : 'standart';
            blobCacheKey = "".concat(userPrincipalName, ":").concat(blobCacheKeySuffix);
            inFlight = inFlightPhotoRequests[blobCacheKey];
            if (inFlight) {
                attachPhotoRequestConsumer(blobCacheKey, options === null || options === void 0 ? void 0 : options.signal);
                return [
                    2,
                    inFlight
                ];
            }
            // Built and registered synchronously — before the getClientConfig await — so callers arriving during
            // the config round-trip join this request rather than opening a second one.
            controller = new AbortController();
            inFlightPhotoAbortControllers[blobCacheKey] = controller;
            attachPhotoRequestConsumer(blobCacheKey, options === null || options === void 0 ? void 0 : options.signal);
            request = getClientConfig().then(function(param) {
                var userPhotoServiceEnabled = param.userPhotoServiceEnabled;
                return userPhotoServiceEnabled ? fetchPhotoFromEndpoint(userPrincipalName, type, dimensions, blobCacheKey, controller.signal) : fetchPhotoFromGraph(userPrincipalName, type, dimensions, blobCacheKey, controller.signal);
            }).finally(function() {
                delete inFlightPhotoRequests[blobCacheKey];
                delete inFlightPhotoAbortControllers[blobCacheKey];
                delete inFlightPhotoScopedSignals[blobCacheKey];
                delete inFlightPhotoUnscopedConsumers[blobCacheKey];
            });
            inFlightPhotoRequests[blobCacheKey] = request;
            return [
                2,
                request
            ];
        });
    })();
};
/** Aborts in-flight `/api/user/photo` (and legacy Graph photo) fetches so pagination can claim HTTP/1.1 slots. */ export var cancelInFlightProfilePhotoRequests = function cancelInFlightProfilePhotoRequests() {
    Object.values(inFlightPhotoAbortControllers).forEach(function(controller) {
        return controller.abort();
    });
};
/** Aborts in-flight Graph presence fetches (separate origin; still clears client work on page change). */ export var cancelInFlightPresenceRequests = function cancelInFlightPresenceRequests() {
    Object.values(inFlightPresenceAbortControllers).forEach(function(controller) {
        return controller.abort();
    });
};
export var evictProfilePictureInmemoryCache = function evictProfilePictureInmemoryCache(userPrincipalName) {
    var _ref;
    var _Object_keys;
    var keys = (_ref = (_Object_keys = Object.keys(profilePicturesInMemoryCache)) === null || _Object_keys === void 0 ? void 0 : _Object_keys.filter(function(it) {
        return it.startsWith(userPrincipalName);
    })) !== null && _ref !== void 0 ? _ref : [];
    var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
    try {
        for(var _iterator = keys[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
            var key = _step.value;
            var url = profilePicturesInMemoryCache === null || profilePicturesInMemoryCache === void 0 ? void 0 : profilePicturesInMemoryCache[key];
            try {
                window.URL.revokeObjectURL(url);
            } catch (unused) {}
            delete profilePicturesInMemoryCache[key];
        }
    } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
    } finally{
        try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
                _iterator.return();
            }
        } finally{
            if (_didIteratorError) {
                throw _iteratorError;
            }
        }
    }
};
export function getProfilePic(userPrincipalName, options) {
    return _async_to_generator(function() {
        var _ref, _inMemoryCacheKeySuffixesGroupedByCacheType_, blobCacheKeySuffix, blobCacheKey, pic;
        return _ts_generator(this, function(_state) {
            if (!userPrincipalName) return [
                2,
                Promise.resolve('')
            ];
            blobCacheKeySuffix = (_ref = inMemoryCacheKeySuffixesGroupedByCacheType === null || inMemoryCacheKeySuffixesGroupedByCacheType === void 0 ? void 0 : (_inMemoryCacheKeySuffixesGroupedByCacheType_ = inMemoryCacheKeySuffixesGroupedByCacheType[0]) === null || _inMemoryCacheKeySuffixesGroupedByCacheType_ === void 0 ? void 0 : _inMemoryCacheKeySuffixesGroupedByCacheType_.call(inMemoryCacheKeySuffixesGroupedByCacheType, '')) !== null && _ref !== void 0 ? _ref : 'standart';
            blobCacheKey = "".concat(userPrincipalName, ":").concat(blobCacheKeySuffix);
            pic = profilePicturesInMemoryCache === null || profilePicturesInMemoryCache === void 0 ? void 0 : profilePicturesInMemoryCache[blobCacheKey];
            if (pic === 'USER_PHOTO_NOT_EXISTS') return [
                2,
                Promise.resolve('')
            ];
            return [
                2,
                pic ? Promise.resolve(pic) : callGraph(userPrincipalName, 0, '96x96', options)
            ];
        });
    })();
}
export function getProfilePicV(userPrincipalName) {
    var dimensions = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : '96x96', options = arguments.length > 2 ? arguments[2] : void 0;
    return _async_to_generator(function() {
        var _ref, _inMemoryCacheKeySuffixesGroupedByCacheType_, blobCacheKeySuffix, blobCacheKey, pic;
        return _ts_generator(this, function(_state) {
            if (!userPrincipalName) return [
                2,
                Promise.resolve('')
            ];
            if (userPrincipalName.startsWith('demo') && /^demo.*@teamflect\.com$/.test(userPrincipalName === null || userPrincipalName === void 0 ? void 0 : userPrincipalName.toString())) return [
                2,
                Promise.resolve('https://teamblee.blob.core.windows.net/teamflect/logo_small_red.png')
            ];
            blobCacheKeySuffix = (_ref = inMemoryCacheKeySuffixesGroupedByCacheType === null || inMemoryCacheKeySuffixesGroupedByCacheType === void 0 ? void 0 : (_inMemoryCacheKeySuffixesGroupedByCacheType_ = inMemoryCacheKeySuffixesGroupedByCacheType[1]) === null || _inMemoryCacheKeySuffixesGroupedByCacheType_ === void 0 ? void 0 : _inMemoryCacheKeySuffixesGroupedByCacheType_.call(inMemoryCacheKeySuffixesGroupedByCacheType, dimensions)) !== null && _ref !== void 0 ? _ref : dimensions;
            blobCacheKey = "".concat(userPrincipalName, ":").concat(blobCacheKeySuffix);
            pic = profilePicturesInMemoryCache === null || profilePicturesInMemoryCache === void 0 ? void 0 : profilePicturesInMemoryCache[blobCacheKey];
            if (pic === 'USER_PHOTO_NOT_EXISTS') return [
                2,
                Promise.resolve('')
            ];
            return [
                2,
                pic ? Promise.resolve(pic) : callGraph(userPrincipalName, 1, dimensions, options)
            ];
        });
    })();
}
export function getProfilePicLarge(userPrincipalName) {
    return _async_to_generator(function() {
        var _ref, _inMemoryCacheKeySuffixesGroupedByCacheType_, blobCacheKeySuffix, blobCacheKey, pic;
        return _ts_generator(this, function(_state) {
            if (!userPrincipalName) return [
                2,
                Promise.resolve('')
            ];
            blobCacheKeySuffix = (_ref = inMemoryCacheKeySuffixesGroupedByCacheType === null || inMemoryCacheKeySuffixesGroupedByCacheType === void 0 ? void 0 : (_inMemoryCacheKeySuffixesGroupedByCacheType_ = inMemoryCacheKeySuffixesGroupedByCacheType[2]) === null || _inMemoryCacheKeySuffixesGroupedByCacheType_ === void 0 ? void 0 : _inMemoryCacheKeySuffixesGroupedByCacheType_.call(inMemoryCacheKeySuffixesGroupedByCacheType, '')) !== null && _ref !== void 0 ? _ref : 'large';
            blobCacheKey = "".concat(userPrincipalName, ":").concat(blobCacheKeySuffix);
            pic = profilePicturesInMemoryCache === null || profilePicturesInMemoryCache === void 0 ? void 0 : profilePicturesInMemoryCache[blobCacheKey];
            if (pic === 'USER_PHOTO_NOT_EXISTS') return [
                2,
                Promise.resolve('')
            ];
            return [
                2,
                pic ? Promise.resolve(pic) : callGraph(userPrincipalName, 2)
            ];
        });
    })();
}
export function fetchPresence(userId, accessToken) {
    return _async_to_generator(function() {
        var _inFlightPresenceAbortControllers_id, id, controller, response, data, e;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    if (!userId || !accessToken) {
                        return [
                            2,
                            PersonaPresence.none
                        ];
                    }
                    id = String(userId);
                    (_inFlightPresenceAbortControllers_id = inFlightPresenceAbortControllers[id]) === null || _inFlightPresenceAbortControllers_id === void 0 ? void 0 : _inFlightPresenceAbortControllers_id.abort();
                    controller = new AbortController();
                    inFlightPresenceAbortControllers[id] = controller;
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        4,
                        5,
                        6
                    ]);
                    return [
                        4,
                        fetch("https://graph.microsoft.com/v1.0/users/".concat(encodeURIComponent(id), "/presence"), {
                            headers: {
                                Authorization: "Bearer ".concat(accessToken)
                            },
                            signal: controller.signal
                        })
                    ];
                case 2:
                    response = _state.sent();
                    if (!response.ok) {
                        return [
                            2,
                            PersonaPresence.none
                        ];
                    }
                    return [
                        4,
                        response.json()
                    ];
                case 3:
                    data = _state.sent();
                    // Presence of each activity values
                    // https://learn.microsoft.com/en-us/graph/api/resources/presence?view=graph-rest-1.0#properties
                    switch(data.activity){
                        case 'Offline':
                            return [
                                2,
                                PersonaPresence.offline
                            ];
                        case 'Available':
                            return [
                                2,
                                PersonaPresence.online
                            ];
                        case 'BeRightBack':
                            return [
                                2,
                                PersonaPresence.away
                            ];
                        case 'Away':
                            return [
                                2,
                                PersonaPresence.away
                            ];
                        case 'DoNotDisturb':
                            return [
                                2,
                                PersonaPresence.dnd
                            ];
                        case 'Blocked':
                            return [
                                2,
                                PersonaPresence.blocked
                            ];
                        case 'Busy':
                            return [
                                2,
                                PersonaPresence.busy
                            ];
                        case 'InACall':
                        case 'InAConferenceCall':
                        case 'InAMeeting':
                        case 'Presenting':
                            return [
                                2,
                                PersonaPresence.busy
                            ];
                        case 'Inactive':
                            return [
                                2,
                                PersonaPresence.away
                            ];
                        case 'OutOfOffice':
                            return [
                                2,
                                PersonaPresence.away
                            ];
                        case 'PresenceUnknown':
                            return [
                                2,
                                PersonaPresence.none
                            ];
                        default:
                            return [
                                2,
                                PersonaPresence.none
                            ];
                    }
                    return [
                        3,
                        6
                    ];
                case 4:
                    e = _state.sent();
                    return [
                        2,
                        PersonaPresence.none
                    ];
                case 5:
                    if (inFlightPresenceAbortControllers[id] === controller) {
                        delete inFlightPresenceAbortControllers[id];
                    }
                    return [
                        7
                    ];
                case 6:
                    return [
                        2
                    ];
            }
        });
    })();
}
